/* eslint-disable react-refresh/only-export-components */
import { api, setTokenProvider } from "@/lib/api";
import { setSaasSpendTokenProvider } from "@/lib/saasSpendApi";
import type { AdminRole, AdminUser } from "@a7y/types";
import { useUser, useAuth } from "@clerk/clerk-react";
import { createContext, ReactNode, useCallback, useEffect, useRef, useState } from "react";

export interface AdminAuthContextType {
  user: AdminUser | null;
  loading: boolean;
  /** A7Y-4957: the caller's admin role; `null` when signed out or not an admin. */
  role: AdminRole | null;
  isSuperAdmin: boolean;
  authError: string | null;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

export const AdminAuthContext = createContext<AdminAuthContextType | undefined>(
  undefined
);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const { user: clerkUser, isLoaded: isClerkLoaded } = useUser();
  const { signOut, getToken } = useAuth();
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const tokenProviderSet = useRef(false);

  // Register the token provider for API calls once.
  // getToken is intentionally excluded from deps — Clerk returns a new reference
  // every render, but the function itself is stable. Including it would re-fire
  // the effect on every render.
  useEffect(() => {
    if (!tokenProviderSet.current) {
      // Forward the options through: `{ skipCache: true }` is what makes the
      // clients' one-shot 401 recovery mint a NEW Clerk JWT rather than being
      // handed the expired one again from Clerk's in-memory cache.
      setTokenProvider((opts) => getToken(opts));
      setSaasSpendTokenProvider((opts) => getToken(opts));
      tokenProviderSet.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resolve the admin role when the Clerk user changes
  useEffect(() => {
    if (!isClerkLoaded) return;

    if (!clerkUser) {
      // Synchronising with Clerk, an external auth system, whose loaded/user
      // state arrives outside React. The signed-out branch shares the same
      // three state slots as the async getMe() branch below, so deriving it
      // during render would mean two sources of truth for adminUser.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setAdminUser(null);
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await api.getMe();
        if (!cancelled) {
          setAdminUser(data.user);
          setAuthError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setAdminUser(null);
          const msg = err instanceof Error ? err.message : String(err);
          if (msg.includes("500")) {
            setAuthError("Unable to reach the admin API. Is the a7y-api dev server running?");
          } else if (msg.includes("403")) {
            setAuthError(null); // Genuine "not an admin" — no special error
          } else {
            setAuthError(msg);
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clerkUser, isClerkLoaded]);

  const logout = useCallback(async () => {
    await signOut();
    setAdminUser(null);
    setAuthError(null);
  }, [signOut]);

  // The role comes from the server-resolved user (`/admin/auth/me`), so there is
  // one source for it; `isSuperAdmin` is derived, never stored separately.
  const role = adminUser?.role ?? null;
  const value: AdminAuthContextType = {
    user: adminUser,
    loading,
    role,
    isSuperAdmin: role === "super_admin",
    authError,
    logout,
    getToken,
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}
